import type { JSX, VoidComponent } from "solid-js";
import { mergeProps, Show } from "solid-js";
import { Checkbox as ArkCheckbox } from "@ark-ui/solid";
import InfoButton from "../infoButton/InfoButton";
import { Icon } from "../icon/Icon";
import { RequiredIndicator } from "../inputs/Input";
import Text from "../text/Text.tsx";

export type CheckedState = boolean | "indeterminate";

type CheckboxProps = {
  label?: string | JSX.Element;
  required?: boolean;
  checked: CheckedState;
  setChecked: (value: CheckedState) => void;
  disabled?: boolean;
  info?: Explainer;
  block?: boolean;
  ariaLabel?: string;
};

type Explainer = {
  title: string;
  alt: string;
  description: string;
};

export const Checkbox: VoidComponent<CheckboxProps> = (rawProps) => {
  const props = mergeProps({ required: false, info: null, block: false, disabled: false }, rawProps);

  return (
    <ArkCheckbox.Root
      checked={props.checked}
      onCheckedChange={(value) => props.setChecked(value.checked)}
      disabled={props.disabled}
      // Relative is needed for the hidden ArkSwitch
      classList={{ "relative flex gap-2": true, "w-full justify-between": props.block }}
      aria-label={props.ariaLabel ?? ""}
    >
      <div class="flex min-w-0 shrink gap-2">
        <ArkCheckbox.HiddenInput class="peer" />
        <CheckboxIndicator />
        <Show when={props.label}>
          {(label) => (
            <ArkCheckbox.Label class="inline-flex">
              <Text
                text={label()}
                truncate={4}
                class="shrink text-base-regular"
                classList={{ "text-secondary": !props.disabled, "text-disabled": props.disabled }}
              />
              <RequiredIndicator required={props.required} />
            </ArkCheckbox.Label>
          )}
        </Show>
      </div>
      <Show when={props.info}>
        {(info) => (
          <div class="my-0.5">
            <InfoButton title={info().title} description={info().description} ariaLabel={info().alt} />
          </div>
        )}
      </Show>
    </ArkCheckbox.Root>
  );
};

export const CheckboxIndicator = () => (
  <ArkCheckbox.Control class="relative my-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-sm border border-default bg-surface-default peer-focus-visible:outline-default checked:border-0 checked:bg-surface-active-brand-harsh indeterminate:border-0 indeterminate:bg-surface-active-brand-harsh disabled:bg-surface-disabled checked:disabled:bg-surface-active-disabled">
    <ArkCheckbox.Indicator>
      <Icon name="check" type="solid" size="xs" class="icon-inverse" />
    </ArkCheckbox.Indicator>
    <ArkCheckbox.Indicator indeterminate>
      <Icon name="minus" type="solid" size="xs" class="icon-inverse" />
    </ArkCheckbox.Indicator>
  </ArkCheckbox.Control>
);
